import React, { useState, useContext, useEffect } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';

const formatReportDay = (value) => {
  if (!value) {
    const today = new Date();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(value).slice(0, 10);
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date || !months[month - 1]) return day;
  return `${months[month - 1]} ${date}, ${year}`;
};

const GoalReportForm = ({ show, onClose, goal, refreshGoals }) => {
  const { user } = useContext(MyContext);
  const [reportText, setReportText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const targetDate = goal?.report_target_date
    ? String(goal.report_target_date).slice(0, 10)
    : '';

  useEffect(() => {
    if (show) {
      setReportText('');
      setError(null);
      setSuccess(null);
    }
  }, [show, goal?.id, targetDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!reportText.trim()) {
      setError('Please provide your progress report');
      return;
    }
    
    try {
      setLoading(true);
      const payload = {
        goal_id: goal.id,
        user_id: user.user_id,
        report_text: reportText
      };
      if (targetDate) {
        payload.report_date = targetDate;
      }

      const response = await fetch(`${backendUrl}/submit_report.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      if (data.success) {
        setSuccess('Report submitted successfully!');
        refreshGoals();
        setTimeout(() => {
          onClose();
          setSuccess(null);
          setReportText('');
        }, 1500);
      } else {
        setError(data.message || data.messages || 'Failed to submit report');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Submit Progress Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <h5>{goal.title}</h5>
        <p className="text-muted">
          Reporting for {formatReportDay(targetDate)}
        </p>
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Your Progress</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              placeholder="How are you progressing with this goal? What challenges are you facing?"
              required
            />
          </Form.Group>
          
          <div className="d-flex justify-content-end">
            <Button 
              variant="secondary" 
              onClick={onClose}
              className="me-2"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                  Submit Report
                </>
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default GoalReportForm;
